import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowDownIcon, ArrowUpIcon, ShieldAlertIcon } from 'lucide-react';
import {
  account,
  analytics,
  type AnalyticsMetric,
  type AnalyticsOverview,
  type AnalyticsWork,
  type MetricFormat,
} from '@/lib/bridge';
import { describeError } from '@/lib/errorMessage';
import { useAccounts } from '@/lib/useAccounts';
import { useAppInfo } from '@/lib/useAppInfo';
import { useAppReady } from '@/lib/useAppReady';
import { useGroupedSurface } from '@/lib/useGroupedSurface';
import { AccountCard } from '@/components/AccountCard';
import { RowList } from '@/components/RowList';
import { SectionHeader } from '@/components/SectionHeader';
import { Segmented } from '@/components/Segmented';
import { PageShell } from '@/components/PageShell';
import { MetricGridSkeleton, PageSkeleton, SkeletonCard } from '@/components/skeletons';
import { Card } from '@/components/ui/card';
import { CARD, MUTED } from '@/components/ui';
import { cn } from '@/lib/utils';

/**
 * 薯薯全能通 · 数据分析。
 *
 * 版式照搬搬小音 `pages/bxy/DataPage.tsx`：分析账号 → 全部账号合计 → 当前账号指标宫格
 * （近 7 / 30 天，点选看每日趋势）→ 作品榜。区别是**指标不写死**：标题、格式、越低越好
 * 都由原生 `analytics.overview` 随数据下发（薯薯的通用指标目录），接新平台 / 新账号类型
 * 只要原生多给指标，页面不用改。数据全部来自 App 本地库。
 */

type SortKey = 'views' | 'likes' | 'comments' | 'collects';

const SORTS: [SortKey, string][] = [
  ['views', '播放'],
  ['likes', '点赞'],
  ['comments', '评论'],
  ['collects', '收藏'],
];

/** 作品行的两行指标：基础互动 + 深度指标（有才显示）。 */
const BASIC_LINE: [string, string, MetricFormat][] = [
  ['views', '播放', 'count'],
  ['likes', '点赞', 'count'],
  ['comments', '评论', 'count'],
  ['collects', '收藏', 'count'],
];
const DEEP_LINE: [string, string, MetricFormat][] = [
  ['completion_rate', '完播', 'percent'],
  ['bounce_rate_2s', '2s跳出', 'percent'],
  ['homepage_visits', '主页访问', 'count'],
];

/** 全部账号合计 · 近 7 天：取各账号的同名指标相加。 */
const AGGREGATE: [string, string][] = [
  ['daily_views', '总播放'],
  ['followers', '总粉丝'],
  ['daily_posts', '投稿量'],
];

function compact(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 100_000_000) return `${(n / 100_000_000).toFixed(1)}亿`;
  if (abs >= 10_000) return `${(n / 10_000).toFixed(1)}万`;
  return `${Math.round(n)}`;
}

function formatMetric(value: number | null | undefined, format: MetricFormat): string {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const n = Number(value);
  switch (format) {
    case 'percent':
      return `${(n * 100).toFixed(1)}%`;
    case 'seconds':
      return `${n.toFixed(1)}s`;
    case 'signedCount':
      return n > 0 ? `+${compact(n)}` : compact(n);
    default:
      return compact(n);
  }
}

/** `2026-09-28` → `09-28` */
function formatDay(date: string): string {
  return date.length === 10 ? date.slice(5) : date;
}

/** 毫秒时间戳 → `18:32` */
function formatClock(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** 环比：变好 / 变差 / 持平；上一周期没数据时不显示。 */
function Change({ metric }: { metric: AnalyticsMetric }) {
  const { value, previous } = metric;
  if (value == null || previous == null || previous === 0) {
    return <span className="text-caption2 text-muted-foreground/70">较上期 —</span>;
  }
  const change = (value - previous) / Math.abs(previous);
  if (Math.abs(change) < 0.005) {
    return <span className="text-caption2 text-muted-foreground">持平</span>;
  }
  const improved = change > 0 !== metric.lowerIsBetter;
  const Icon = change > 0 ? ArrowUpIcon : ArrowDownIcon;
  return (
    <span
      className={cn(
        'text-caption2 inline-flex items-center gap-0.5 tabular-nums',
        // 黑白灰：方向靠箭头，改善用主色、变差用次要灰（红色只留给错误）
        improved ? 'text-foreground' : 'text-muted-foreground',
      )}
    >
      <Icon className="size-3" />
      {(Math.abs(change) * 100).toFixed(0)}%
    </span>
  );
}

export function ShushuDataPage() {
  const { loading: infoLoading, isNative } = useAppInfo();
  const {
    accounts,
    selectedId,
    setSelectedId,
    error: accountError,
    loading: accountsLoading,
  } = useAccounts();

  const [recentDays, setRecentDays] = useState<7 | 30>(7);
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [metricKey, setMetricKey] = useState<string | null>(null);
  const [works, setWorks] = useState<AnalyticsWork[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>('views');
  const [aggregate, setAggregate] = useState<{ title: string; value: number }[] | null>(null);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [loadingWorks, setLoadingWorks] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loaded = !infoLoading && !accountsLoading;
  useAppReady(loaded);
  // 与原生页面同色（iOS 分组底色），WebView 嵌在导航栏下不割裂
  useGroupedSurface();

  const currentAccount = accounts.find((a) => a.id === selectedId) ?? null;
  const writable = useMemo(() => accounts.filter((a) => a.canWrite), [accounts]);

  // 选中账号的指标总览
  useEffect(() => {
    if (!currentAccount) {
      setOverview(null);
      return;
    }
    let cancelled = false;
    setLoadingOverview(true);
    setError(null);
    analytics
      .overview(currentAccount.id, recentDays)
      .then((next) => {
        if (!cancelled) setOverview(next);
      })
      .catch((e) => {
        if (!cancelled) setError(describeError(e));
      })
      .finally(() => {
        if (!cancelled) setLoadingOverview(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentAccount?.id, recentDays]);

  // 选中账号的作品
  useEffect(() => {
    if (!currentAccount) {
      setWorks([]);
      return;
    }
    let cancelled = false;
    setLoadingWorks(true);
    analytics
      .works(currentAccount.id, 50)
      .then((res) => {
        if (!cancelled) setWorks(res.items);
      })
      .catch((e) => {
        if (!cancelled) setError(describeError(e));
      })
      .finally(() => {
        if (!cancelled) setLoadingWorks(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentAccount?.id]);

  // 全部账号合计：两个以上正常账号才有意义；只算一次
  useEffect(() => {
    if (writable.length < 2 || aggregate !== null) return;
    let cancelled = false;
    void Promise.allSettled(writable.map((a) => analytics.overview(a.id, 7))).then((results) => {
      if (cancelled) return;
      const overviews = results.flatMap((r) => (r.status === 'fulfilled' ? [r.value] : []));
      const items = AGGREGATE.flatMap(([key, title]) => {
        const values = overviews
          .map((o) => o.metrics.find((m) => m.key === key)?.value)
          .filter((v): v is number => v != null);
        return values.length ? [{ title, value: values.reduce((sum, v) => sum + v, 0) }] : [];
      });
      setAggregate(items);
    });
    return () => {
      cancelled = true;
    };
  }, [writable, aggregate]);

  const switchAccount = useCallback(async () => {
    try {
      setSelectedId((await account.pick('authenticated')).id);
    } catch {
      // 用户取消：保留当前账号
    }
  }, [setSelectedId]);

  const metrics = overview?.metrics ?? [];
  const activeMetric = metrics.find((m) => m.key === metricKey) ?? metrics[0];
  const maxTrend = Math.max(...(activeMetric?.trend ?? []).map((t) => t.value), 1);
  const trendAllZero = !!activeMetric?.trend.length && activeMetric.trend.every((t) => t.value === 0);
  const minTrend = Math.min(...(activeMetric?.trend ?? []).map((t) => t.value), 0);
  // 累计快照（总粉丝等）起伏小，柱高按 min~max 拉开，否则全是等高柱
  const snapshot = activeMetric?.aggregation === 'latest';

  const sortedWorks = useMemo(
    () => [...works].sort((a, b) => (b.metrics[sortKey] ?? -1) - (a.metrics[sortKey] ?? -1)),
    [works, sortKey],
  );

  const metricLine = (work: AnalyticsWork, fields: [string, string, MetricFormat][]) =>
    fields
      .filter(([key]) => work.metrics[key] != null)
      .map(([key, label, format]) => `${label} ${formatMetric(work.metrics[key], format)}`)
      .join(' · ');

  return (
    <PageShell title="数据分析" description="账号播放 / 粉丝 / 互动趋势与单篇作品表现">
      {currentAccount ? (
        <AccountCard
          account={currentAccount}
          label={[currentAccount.platformLabel, currentAccount.handle].filter(Boolean).join(' · ') || '分析账号'}
          showId={false}
          detail={writable.length > 1 ? `${writable.length} 个账号可切换` : undefined}
          onClick={writable.length > 1 ? () => void switchAccount() : undefined}
        />
      ) : null}

      {!isNative && loaded ? (
        <Card className={CARD}>
          <p className="text-muted-foreground text-xs">
            当前不在 App 内。浏览器里单独调试请加 <code>?mock=1</code>。
          </p>
        </Card>
      ) : null}

      {loaded && isNative && !currentAccount ? (
        <Card className={CARD}>
          <div className="flex items-center gap-2">
            <ShieldAlertIcon className="text-primary size-5 shrink-0" />
            <strong className="text-sm font-semibold">没有可分析的账号</strong>
          </div>
          <p className="text-muted-foreground mt-1 text-xs">请先在 App 内登录一个平台账号。</p>
        </Card>
      ) : null}

      {/* 全部账号合计 */}
      {aggregate && aggregate.length > 0 ? (
        <Card className={CARD}>
          <SectionHeader title="全部账号" trailing="近 7 天" />
          <div className="grid grid-cols-3 gap-2 text-center">
            {aggregate.map((item) => (
              <div key={item.title}>
                <div className="text-title2 font-semibold tabular-nums">{compact(item.value)}</div>
                <div className="text-caption1 text-muted-foreground">{item.title}</div>
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      {/* 当前账号指标 */}
      {currentAccount ? (
        <Card className={CARD}>
          <SectionHeader
            title="当前账号"
            trailing={
              loadingOverview && !overview
                ? '读取中…'
                : overview?.updatedAt
                  ? `更新于 ${formatClock(overview.updatedAt)}`
                  : undefined
            }
          />
          <Segmented
            value={recentDays}
            options={[
              [7, '近 7 天'],
              [30, '近 30 天'],
            ]}
            onChange={setRecentDays}
          />

          {(loadingOverview && !overview) || metrics.length > 0 ? (
            <div className="grid grid-cols-3 gap-2">
              {loadingOverview && !overview ? (
                <MetricGridSkeleton count={6} />
              ) : (
                metrics.map((metric) => (
                  <button
                    key={metric.key}
                    type="button"
                    onClick={() => setMetricKey(metric.key)}
                    className={cn(
                      'rounded-control bg-muted/60 px-2 py-2.5 text-left transition-colors',
                      metric.key === activeMetric?.key ? 'ring-1 ring-primary' : 'active:bg-accent',
                    )}
                  >
                    <div className="text-caption1 text-muted-foreground truncate">{metric.title}</div>
                    <div className="text-subheadline font-semibold tabular-nums truncate">
                      {formatMetric(metric.value, metric.format)}
                    </div>
                    <Change metric={metric} />
                  </button>
                ))
              )}
            </div>
          ) : null}

          {overview && metrics.length === 0 ? (
            <p className="text-muted-foreground text-footnote leading-snug">
              这个账号还没有指标数据，回到 App 下拉同步后再来看。
            </p>
          ) : null}

          {/* 选中指标的每日趋势 */}
          {activeMetric?.trend.length ? (
            <div>
              <p className={MUTED}>{activeMetric.title} · 每日趋势</p>
              {/* 每天都是 0 时不留大片空白（像没读到数据）：图表压扁成一排底座 + 数字，并明说是 0 */}
              <div className={cn('mt-2 flex gap-1', trendAllZero ? 'h-auto' : 'h-28')}>
                {activeMetric.trend.map((point) => {
                  const floor = snapshot ? minTrend * 0.98 : 0;
                  const span = Math.max(maxTrend - floor, 1e-9);
                  // 用确定像素高度：百分比高度相对 auto 父级会塌缩成 0
                  const barPx = Math.round(((point.value - floor) / span) * 64) + 4;
                  return (
                    <div
                      key={point.date}
                      className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1"
                    >
                      {activeMetric.trend.length <= 7 ? (
                        <span className="text-caption2 text-muted-foreground tabular-nums">
                          {formatMetric(point.value, activeMetric.format)}
                        </span>
                      ) : null}
                      <div className="bg-foreground/70 w-full rounded-t" style={{ height: `${barPx}px` }} />
                      <span className="text-caption2 text-muted-foreground">
                        {activeMetric.trend.length <= 7 ? formatDay(point.date) : ''}
                      </span>
                    </div>
                  );
                })}
              </div>
              {trendAllZero ? (
                <p className="text-muted-foreground mt-2 text-footnote">
                  近 {activeMetric.trend.length} 天每天都是 0，数据已同步，有变化后会在这里显示。
                </p>
              ) : null}
            </div>
          ) : null}
        </Card>
      ) : null}

      {/* 作品榜 */}
      {currentAccount ? (
        <Card className={CARD}>
          <SectionHeader
            title="作品表现"
            trailing={loadingWorks && works.length === 0 ? '读取中…' : `${works.length} 篇`}
          />
          <Segmented value={sortKey} options={SORTS} onChange={setSortKey} />

          {works.length === 0 && !loadingWorks ? (
            <p className="text-muted-foreground text-footnote leading-snug">
              这个账号暂时没有作品数据，确认登录有效后回到 App 下拉同步。
            </p>
          ) : null}

          <RowList>
            {sortedWorks.map((work) => {
              const basic = metricLine(work, BASIC_LINE);
              const deep = metricLine(work, DEEP_LINE);
              return (
                <div
                  key={work.id}
                  className="bg-muted/60 rounded-control px-3 py-2.5 ios:rounded-none ios:bg-transparent ios:px-0 ios:py-3"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    {work.cover ? (
                      <img
                        src={work.cover}
                        alt=""
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        className="size-9 shrink-0 rounded object-cover ios:size-11 ios:rounded-lg"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : null}
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-subheadline ios:text-body">{work.title || '无标题'}</div>
                      {basic ? (
                        <div className="text-caption1 text-muted-foreground tabular-nums ios:text-footnote">{basic}</div>
                      ) : null}
                      {deep ? <div className="text-caption1 text-primary tabular-nums ios:text-footnote">{deep}</div> : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </RowList>

          {loadingWorks && works.length === 0 ? <SkeletonCard rows={3} title={false} /> : null}
        </Card>
      ) : null}

      {(error ?? accountError) ? (
        <p className="text-destructive px-1 text-sm">{error ?? accountError}</p>
      ) : null}

      {!loaded ? <PageSkeleton kind="data" /> : null}
    </PageShell>
  );
}
